package org.maplibre.compose.demoapp.demos.snapshotter

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.hoverable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.absoluteOffset
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.ui.AbsoluteAlignment
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.RoundRect
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.PathFillType
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.LayoutCoordinates
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.semantics.CustomAccessibilityAction
import androidx.compose.ui.semantics.SemanticsActions
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.DpSize
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import kotlin.math.min
import kotlin.math.roundToInt

/** Touch target of a corner handle; the visual dot is much smaller. */
private val HandleTouchTarget = 48.dp
private val HandleDotSize = 16.dp

/** Arm length cap for the corner brackets, so small frames keep their thirds grid readable. */
private val BracketArm = 26.dp

/** The viewfinder fills the space that the surrounding column leaves above its controls. */
@Composable
internal fun SnapshotFrame(
  aspect: SnapshotAspect,
  modifier: Modifier = Modifier,
  onPositioned: (LayoutCoordinates) -> Unit,
) {
  var preferredSize by remember { mutableStateOf<DpSize?>(null) }
  BoxWithConstraints(modifier.fillMaxSize()) {
    val safe = DpSize(maxWidth, maxHeight)
    val size =
      fitFrameToSafeArea(preferredSize ?: DpSize(maxWidth * 0.62f, maxHeight * 0.62f), safe, aspect)
    val frame = centeredFrame(size, safe)
    Box(Modifier.align(Alignment.Center).size(size).onGloballyPositioned(onPositioned))
    if (size.width >= HandleTouchTarget && size.height >= HandleTouchTarget) {
      FrameCorner.entries.forEach { corner ->
        FrameHandle(
          corner,
          frame,
          modifier = Modifier.align(AbsoluteAlignment.TopLeft),
          onDrag = { drag ->
            preferredSize =
              resizedFrame(
                fitFrameToSafeArea(preferredSize ?: size, safe, aspect),
                corner,
                drag,
                safe,
                aspect,
              )
          },
        )
      }
    }
  }
}

/** Full-map drawing uses the measured frame's map-relative pixel bounds. */
@Composable
internal fun SnapshotScrim(frame: () -> Rect?) {
  val scrimPath = remember { Path() }
  Canvas(Modifier.fillMaxSize()) {
    val framePx = frame()
    scrimPath.rewind()
    scrimPath.fillType = PathFillType.EvenOdd
    scrimPath.addRect(Rect(Offset.Zero, size))
    if (framePx != null) scrimPath.addRoundRect(RoundRect(framePx, CornerRadius(4.dp.toPx())))
    drawPath(scrimPath, Color.Black.copy(alpha = 0.38f))
    if (framePx == null) return@Canvas

    val gridColor = Color.White.copy(alpha = 0.3f)
    val gridStroke = 1.dp.toPx()
    for (i in 1..2) {
      val x = framePx.left + framePx.width * i / 3f
      val y = framePx.top + framePx.height * i / 3f
      drawLine(gridColor, Offset(x, framePx.top), Offset(x, framePx.bottom), gridStroke)
      drawLine(gridColor, Offset(framePx.left, y), Offset(framePx.right, y), gridStroke)
    }

    val arm = min(BracketArm.toPx(), min(framePx.width, framePx.height) / 3f)
    // A dark halo keeps the white brackets readable on bright basemaps.
    drawBrackets(framePx, arm, Color.Black.copy(alpha = 0.35f), 5.5f.dp.toPx())
    drawBrackets(framePx, arm, Color.White, 3.dp.toPx())
  }
}

private fun DrawScope.drawBrackets(frame: Rect, arm: Float, color: Color, strokeWidth: Float) {
  for (corner in FrameCorner.entries) {
    val anchor =
      Offset(
        x = if (corner.signX < 0) frame.left else frame.right,
        y = if (corner.signY < 0) frame.top else frame.bottom,
      )
    drawLine(
      color,
      start = anchor - Offset(arm * corner.signX, 0f),
      end = anchor,
      strokeWidth = strokeWidth,
      cap = StrokeCap.Round,
    )
    drawLine(
      color,
      start = anchor - Offset(0f, arm * corner.signY),
      end = anchor,
      strokeWidth = strokeWidth,
      cap = StrokeCap.Round,
    )
  }
}

@Composable
private fun FrameHandle(
  corner: FrameCorner,
  frame: Rect,
  modifier: Modifier,
  onDrag: (DpOffset) -> Unit,
) {
  var coordinates by remember { mutableStateOf<LayoutCoordinates?>(null) }
  val interactionSource = remember { MutableInteractionSource() }
  val hovered by interactionSource.collectIsHoveredAsState()
  var dragging by remember { mutableStateOf(false) }
  // The gesture below never restarts, so it must reach the latest onDrag (which closes over the
  // current safe area) rather than the one from first composition.
  val currentOnDrag by rememberUpdatedState(onDrag)
  val scale by
    animateFloatAsState(
      targetValue = if (dragging) 1.35f else if (hovered) 1.15f else 1f,
      animationSpec = MaterialTheme.motionScheme.fastSpatialSpec(),
      label = "handle scale",
    )
  val center =
    Offset(
      x = if (corner.signX < 0) frame.left else frame.right,
      y = if (corner.signY < 0) frame.top else frame.bottom,
    )
  val description =
    when (corner) {
      FrameCorner.TopLeft -> "Resize frame from top left"
      FrameCorner.TopRight -> "Resize frame from top right"
      FrameCorner.BottomLeft -> "Resize frame from bottom left"
      FrameCorner.BottomRight -> "Resize frame from bottom right"
    }
  // Drags are pointer-only, so grow and shrink also get semantics actions assistive tech can
  // invoke. A nudge mirrors a 24 dp diagonal drag through this corner.
  val grow = DpOffset(24.dp * corner.signX, 24.dp * corner.signY)
  val shrink = DpOffset(-24.dp * corner.signX, -24.dp * corner.signY)
  Box(
    modifier =
      modifier
        .absoluteOffset {
          IntOffset(
            (center.x.dp.toPx() - HandleTouchTarget.toPx() / 2).roundToInt(),
            (center.y.dp.toPx() - HandleTouchTarget.toPx() / 2).roundToInt(),
          )
        }
        .size(HandleTouchTarget)
        .onGloballyPositioned { coordinates = it }
        .semantics {
          contentDescription = description
          set(
            SemanticsActions.CustomActions,
            listOf(
              CustomAccessibilityAction(
                label = "Grow frame",
                action = {
                  onDrag(grow)
                  true
                },
              ),
              CustomAccessibilityAction(
                label = "Shrink frame",
                action = {
                  onDrag(shrink)
                  true
                },
              ),
            ),
          )
        }
        .pointerHoverIcon(PointerIcon.Hand)
        .hoverable(interactionSource)
        .pointerInput(corner) {
          var previousPosition: Offset? = null
          detectDragGestures(
            onDragStart = {
              dragging = true
              previousPosition = null
            },
            onDragEnd = {
              dragging = false
            },
            onDragCancel = {
              dragging = false
            },
          ) { change, dragAmount ->
            change.consume()
            // The handle moves during the gesture; measure pointer travel in fixed root
            // coordinates.
            val position = coordinates?.localToRoot(change.position) ?: change.position
            val delta = previousPosition?.let { position - it } ?: dragAmount
            previousPosition = position
            currentOnDrag(DpOffset(delta.x.toDp(), delta.y.toDp()))
          }
        },
    contentAlignment = Alignment.Center,
  ) {
    Box(
      Modifier.size(HandleDotSize)
        .graphicsLayer {
          scaleX = scale
          scaleY = scale
        }
        .shadow(3.dp, CircleShape)
        .background(MaterialTheme.colorScheme.primary, CircleShape)
        .border(2.dp, Color.White, CircleShape)
    )
  }
}
